/**
 * Error boundary for the Chatroom (and any other heavy view that should
 * survive a render-time bug without nuking the whole SPA).
 *
 * If a child throws during render, we catch it, surface a friendly
 * message + the error.message + a Reload button, and log the full stack
 * to the console for the engineer to copy. We deliberately do NOT
 * render the stack in the UI — it can leak file paths and component
 * names that aren't useful to a typical end-user.
 *
 * React 19 has experimental hooks-based error boundaries, but the
 * stable pattern is still a class component with componentDidCatch.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  readonly children: ReactNode
}

interface State {
  readonly error: Error | null
}

export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface stack to console so the engineer (or the user reporting
    // the bug) can copy it. We don't ship this to the server in MVP —
    // a Sentry adapter slots in later as a one-line constructor swap.
    // eslint-disable-next-line no-console
    console.error('Chatroom render error', error, info.componentStack)
  }

  private reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-rose-200 p-8 mt-10">
          <h1 className="text-lg font-semibold text-rose-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-700 mb-4">
            The chat surface hit an unexpected error and stopped rendering. Your
            session and any files you uploaded are safe — the error is in the
            browser only.
          </p>
          <p className="text-xs text-slate-500 mb-4 font-mono break-words">
            {this.state.error.message}
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:bg-slate-50"
            >
              Reload page
            </button>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            If this keeps happening, copy the message above and the stack from
            your browser console and share it.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}
